import type { AnalyseSeite } from '@awf/shared';

/**
 * Hinweise zum Fokus-Keyword einer Analyse-Seite (Regel vom 2026-10-16 in `Generate audit_id`).
 * Formuliert aus Sicht der Redaktion (Rueckmeldung Martin 2026-10-16): was passiert ist, was es
 * fuer die Seite bedeutet, was Sie tun koennen -- ohne Begriffe aus der Pipeline.
 */

/** Grund, warum auf dieser Seite nur Verwerfen geht. Dieselbe Sperre setzt das Backend (409). */
export const OHNE_KEYWORD_SPERRE =
  'Ohne Fokus-Keyword lässt sich diese Seite nicht optimieren. Sie können sie verwerfen.';

function keywordSatz(seite: Pick<AnalyseSeite, 'fokusKeyword'>): string {
  return seite.fokusKeyword
    ? `Das Fokus-Keyword „${seite.fokusKeyword}“`
    : 'Das Fokus-Keyword dieser Analyse';
}

/** Ein Satz fuer die Seitenliste, oder `undefined`, wenn nichts auffaellig ist. */
export function keywordHinweisKurz(
  seite: Pick<AnalyseSeite, 'keywordHinweis' | 'fokusKeyword'>,
): string | undefined {
  if (seite.keywordHinweis === 'abweichend') {
    return `Bitte prüfen: ${keywordSatz(seite)} passt nicht eindeutig zu dieser Seite.`;
  }
  if (seite.keywordHinweis === 'fehlt') {
    return 'Keine Empfehlungen: Die Analyse hat kein Fokus-Keyword gefunden. Eine Optimierung ist nicht möglich.';
  }
  return undefined;
}

/**
 * Der Kasten auf der Entscheidungsseite. Titel und Text in EINEM Kind: `.eds-alert` ist flex,
 * sonst stuende der Titel als schmale Spalte links neben dem Text (Rueckmeldung 2026-10-16).
 */
export function KeywordHinweisKasten({
  seite,
  entscheidbar,
}: {
  seite: Pick<AnalyseSeite, 'keywordHinweis' | 'fokusKeyword'>;
  /** Nur dann den Weg "verwerfen" nennen (Codex-Review PR #92): fremd, ueberholt oder beendet geht nichts. */
  entscheidbar: boolean;
}) {
  if (seite.keywordHinweis === 'abweichend') {
    return (
      <div className="eds-alert eds-alert--warning" role="status">
        <div>
          <p className="eds-alert__title">Bitte prüfen Sie die Empfehlungen</p>
          <p>
            {keywordSatz(seite)} kommt weder in der Adresse noch in der Überschrift dieser Seite
            vor. Die Empfehlungen wurden trotzdem für diese Seite erstellt, können aber teilweise am
            Thema vorbeigehen. Prüfen Sie sie, bevor Sie die Optimierung starten.
          </p>
        </div>
      </div>
    );
  }
  if (seite.keywordHinweis === 'fehlt') {
    return (
      <div className="eds-alert eds-alert--warning" role="status">
        <div>
          <p className="eds-alert__title">Für diese Seite gibt es keine Empfehlungen</p>
          <p>
            Die Analyse konnte kein Fokus-Keyword bestimmen, also keinen Suchbegriff, auf den die
            Seite ausgerichtet werden soll. Ohne diesen Begriff entstehen keine Empfehlungen, und
            die Optimierung lässt sich nicht starten.
            {entscheidbar && ' Sie können die Seite verwerfen.'}
          </p>
        </div>
      </div>
    );
  }
  return null;
}
